import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApiClient } from '../api/client';
import { MetricCard, PageHeader, PageShell } from '../components/ui';
import { useI18n } from '../i18n/I18nProvider';

type CommunityDto = {
  id: string;
  name: string;
  description: string | null;
  type: string | null;
  users?: { id: string }[];
  groups?: { id: string; description?: string | null }[];
};

export const CommunitiesListPage: React.FC = () => {
  const { request } = useApiClient();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [items, setItems] = useState<CommunityDto[]>([]);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError(null);
    request<CommunityDto[]>('/communities')
      .then(setItems)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [request, reload]);

  const filteredItems = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    return items.filter((community) =>
      !normalizedQuery || `${community.name} ${community.description ?? ''}`.toLocaleLowerCase().includes(normalizedQuery),
    );
  }, [items, query]);
  const memberCount = items.reduce((sum, community) => sum + (community.users?.length ?? 0), 0);
  const groupCount = items.reduce((sum, community) => sum + (community.groups?.length ?? 0), 0);

  return (
    <PageShell>
      <PageHeader
        title={t('communities.title')}
        description={t('communities.description')}
        actions={<Link className="btn btn-primary" to="/communities/new">{t('communities.create')}</Link>}
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <MetricCard label={t('communities.total')} value={loading ? '—' : items.length} tone="violet" />
        <MetricCard label={t('communities.members')} value={loading ? '—' : memberCount} tone="blue" />
        <MetricCard label={t('communities.groups')} value={loading ? '—' : groupCount} tone="green" />
      </div>

      <section className="admin-panel mt-5 p-4 sm:p-5" aria-label={t('communities.search')}>
        <label className="admin-form-label" htmlFor="community-search">{t('communities.search')}</label>
        <input
          id="community-search"
          className="form-control mt-1 w-full sm:max-w-md"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('communities.searchPlaceholder')}
        />
      </section>

      {error ? (
        <div className="admin-panel mt-4 flex flex-col items-start gap-3 p-5" role="alert">
          <p className="text-sm text-red-700">{t('communities.loadError')}</p>
          <button className="btn btn-neutral" onClick={() => setReload((value) => value + 1)}>{t('communities.retry')}</button>
        </div>
      ) : loading ? (
        <div className="admin-panel mt-4 p-5 text-sm text-slate-500" role="status">{t('common.loading')}</div>
      ) : filteredItems.length ? (
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          {filteredItems.map((community) => (
            <button
              key={community.id}
              type="button"
              onClick={() => navigate(`/communities/${community.id}`)}
              className="admin-community-card text-left"
              aria-label={`${t('communities.open')}: ${community.name}`}
            >
              <span className="admin-community-mark" aria-hidden="true">{community.name.slice(0, 1).toLocaleUpperCase()}</span>
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="admin-community-card-title">{community.name}</span>
                  {community.type && <span className="admin-status admin-tone-violet">{t(`communities.type.${community.type}`)}</span>}
                </span>
                <span className="admin-community-card-description">
                  {community.description || t('communities.description')}
                </span>
                <span className="admin-community-card-meta">
                  <span><strong>{community.users?.length ?? 0}</strong> {t('communities.members')}</span>
                  <span><strong>{community.groups?.length ?? 0}</strong> {t('communities.groups')}</span>
                </span>
              </span>
              <svg className="admin-community-card-arrow" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="m9 18 6-6-6-6" /></svg>
            </button>
          ))}
        </div>
      ) : (
        <div className="admin-community-empty mt-4">
          <span className="admin-community-empty-mark" aria-hidden="true">⌂</span>
          <p>{items.length ? t('communities.noMatch') : t('communities.empty')}</p>
          {!items.length && <Link className="btn btn-primary mt-3" to="/communities/new">{t('communities.create')}</Link>}
        </div>
      )}
    </PageShell>
  );
};
